import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Alternator / Starter Replacement",
  "Water Pump / Radiator Cooling Repair",
  "Check Engine Light Diagnostics & Tune-Up",
  "Emergency Jump Start / Battery Replacement",
  "Spare Tire Change",
  "Other Mechanical Service",
];

const TIME_WINDOWS = [
  "First available",
  "Weekday evening (5:30 - 8 PM)",
  "Weekday late evening (8 - 11 PM)",
  "Weekend morning",
  "Weekend afternoon",
  "Weekend evening",
];

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  vehicle: "",
  location: "",
  service: SERVICE_OPTIONS[0],
  preferred_date: "",
  preferred_time: TIME_WINDOWS[0],
};

const inputCls =
  "w-full bg-[#080d15] border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#ff4b2b] focus:outline-none transition-colors";
const labelCls = "block font-mono-lbl text-xs text-slate-300 uppercase mb-2";

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received — we'll call you back with an honest upfront price shortly.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error("Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 px-6 max-w-4xl mx-auto">
      <div className="bg-[#101826] border border-white/15 rounded-2xl p-8 sm:p-12 shadow-2xl">
        <div className="text-center max-w-lg mx-auto mb-10">
          <p className="font-mono-lbl text-xs text-[#00e5ff] uppercase tracking-widest">
            Direct Dispatch Form
          </p>
          <h2 className="text-2xl sm:text-4xl font-black uppercase mt-2">
            Request Mobile Service
          </h2>
          <p className="text-slate-400 text-sm mt-2">
            Get an honest upfront price. We come to your home, office, or highway location.
          </p>
        </div>

        <form onSubmit={onSubmit} data-testid="quote-form" className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote-name" className={labelCls}>Your Name</label>
              <input
                id="quote-name"
                data-testid="quote-name-input"
                type="text"
                placeholder="John Doe"
                value={form.name}
                onChange={update("name")}
                required
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="quote-phone" className={labelCls}>Phone Number</label>
              <input
                id="quote-phone"
                data-testid="quote-phone-input"
                type="tel"
                placeholder="(555) 000-0000"
                value={form.phone}
                onChange={update("phone")}
                required
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote-email" className={labelCls}>Email (For Confirmation)</label>
              <input
                id="quote-email"
                data-testid="quote-email-input"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={update("email")}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="quote-vehicle" className={labelCls}>Vehicle (Year / Make / Model)</label>
              <input
                id="quote-vehicle"
                data-testid="quote-vehicle-input"
                type="text"
                placeholder="2018 Ford F-150"
                value={form.vehicle}
                onChange={update("vehicle")}
                required
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote-location" className={labelCls}>Your Location / City</label>
              <input
                id="quote-location"
                data-testid="quote-location-input"
                type="text"
                placeholder="Dayton, Cincinnati, or street address"
                value={form.location}
                onChange={update("location")}
                required
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="quote-service" className={labelCls}>Service Needed</label>
              <select
                id="quote-service"
                data-testid="quote-service-select"
                value={form.service}
                onChange={update("service")}
                className={inputCls}
              >
                {SERVICE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote-date" className={labelCls}>Preferred Date (Optional)</label>
              <input
                id="quote-date"
                data-testid="quote-date-input"
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={form.preferred_date}
                onChange={update("preferred_date")}
                className={`${inputCls} [color-scheme:dark]`}
              />
            </div>
            <div>
              <label htmlFor="quote-time" className={labelCls}>Preferred Time Window</label>
              <select
                id="quote-time"
                data-testid="quote-time-select"
                value={form.preferred_time}
                onChange={update("preferred_time")}
                className={inputCls}
              >
                {TIME_WINDOWS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            data-testid="quote-submit-button"
            disabled={sending}
            className="w-full bg-[#ff4b2b] hover:bg-[#ff3411] disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-lg font-bold uppercase text-sm tracking-wide transition-colors shadow-lg shadow-[#ff4b2b]/30"
          >
            {sending ? "Sending Request..." : "Request Service Quote Now"}
          </button>
        </form>
      </div>
    </section>
  );
}
